<script setup lang="ts">
import {computed, PropType} from "vue";
import {useI18n} from "vue-i18n";
import {IUserHollandCodesReportData} from "@/pages/Insights/ReportsDetailPage/reportTypes";

const {t} = useI18n();

defineProps({
  reportData: {
    type: Array as PropType<IUserHollandCodesReportData[]>,
    required: true
  },
  loadingData: {
    type: Boolean,
    required: true
  }
});

const headers = computed(
  () => [
    {
      title: t("components.ReportsDetailPage.holland_code_headers.full_name"),
      key: "fullName"
    },
    {
      title: "Tenant",
      key: "tenant"
    },
    {
      title: t("components.ReportsDetailPage.holland_code_headers.primary"),
      key: "primary_code"
    },
    {
      title: t("components.ReportsDetailPage.holland_code_headers.secondary"),
      key: "secondary_code"
    },
    {
      title: t("components.ReportsDetailPage.holland_code_headers.tertiary"),
      key: "tertiary_code"
    },
  ]
);

function getWorkerTypeColor(code: string) {
  if (code === "R")
    return "#fc631d";

  if (code === "I")
    return "#0487ff";

  if (code === "A")
    return "#1eac30";

  if (code === "S")
    return "#ff5d5d";

  if (code === "E")
    return "#514ee4";

  if (code === "C")
    return "#ff3520";
}

</script>

<template>
  <v-data-table
    :items="reportData"
    :headers="headers"
    :loading="loadingData"
    :items-per-page="50"
  >
    <template v-slot:item.fullName="{ item }">
      <span class="font-weight-bold">{{ item.fullName }}</span>

      <div>
        <div v-if="item.email">
          <v-icon size="x-small">far fa-envelope</v-icon>
          {{ item.email }}
        </div>
        <div v-if="item.phone">
          <v-icon size="x-small">far fa-phone</v-icon>
          {{ item.phone }}
        </div>
      </div>
    </template>

    <template v-slot:item.tenant="{ item }">
      <div class="py-4">
        <div class="font-weight-bold text-blue-grey-darken-1 pb-1">{{ item.tenant.title }}</div>
        <div>
          <v-chip size="small" class="font-weight-bold mr-3">
            <v-icon color="grey" class="mr-2">far fa-building</v-icon>
            {{ item.institution.title }}
          </v-chip>
          <v-chip size="small" class="font-weight-bold">
            <v-icon color="grey" class="mr-2">far fa-graduation-cap</v-icon>
            {{ item.cohort.title }}
          </v-chip>
        </div>
      </div>
    </template>

    <template v-slot:item.primary_code="{ item }: { item: IUserHollandCodesReportData}">
      <v-chip :color='getWorkerTypeColor(item["primary_code"])' class="font-weight-bold">{{
        !item["primary_code"]
          ? $t('components.ReportsDetailPage.reports.users_holland_codes_report.no_results')
          : $t(`assessments.holland_code.${item["primary_code"]}`)
      }}
      </v-chip>
    </template>
    <template v-slot:item.secondary_code="{ item }: { item: IUserHollandCodesReportData}">
      <v-chip :color='getWorkerTypeColor(item["secondary_code"])' class="font-weight-bold">
        {{
          !item["secondary_code"]
            ? $t('components.ReportsDetailPage.reports.users_holland_codes_report.no_results')
            : $t(`assessments.holland_code.${item["secondary_code"]}`)
        }}
      </v-chip>
    </template>
    <template v-slot:item.tertiary_code="{ item }: { item: IUserHollandCodesReportData}">
      <v-chip :color='getWorkerTypeColor(item["tertiary_code"])' class="font-weight-bold">
        {{
          !item["tertiary_code"]
            ? $t('components.ReportsDetailPage.reports.users_holland_codes_report.no_results')
            : $t(`assessments.holland_code.${item["tertiary_code"]}`)
        }}
      </v-chip>
    </template>
  </v-data-table>
</template>

<style scoped>

</style>
